import React from "react";
import {
  BookOutlined,
  CompassOutlined,
  SafetyOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import { formatMoney } from "components/metadata/formatMoney";

export type OtherServicePrefix =
  | "standardLibraryPublicationsPaper"
  | "standardLibraryPublicationsDigital"
  | "planningStationWeeklyUpdates"
  | "planningStationEps"
  | "planningStationEpsVas"
  | "planningStationEns"
  | "eNtmSupport"
  | "avcsOptimizationEnc";

export interface OtherServiceDef {
  prefix: OtherServicePrefix;
  product: string;
  service: string;
  subtitle: string;
  hasWifiDongle?: boolean;
  hidden?: boolean;
  usesPayPerUseAmount?: boolean;
}

export const WIFI_DONGLE_VISIBLE = false;

export const OTHER_SERVICE_CATALOG: OtherServiceDef[] = [
  {
    prefix: "standardLibraryPublicationsPaper",
    product: "Standard Library Publications",
    service: "Paper Publications",
    subtitle: "Physical publication service",
  },
  {
    prefix: "standardLibraryPublicationsDigital",
    product: "Standard Library Publications",
    service: "Digital Publications",
    subtitle: "Witherby digital publications",
    hidden: true,
  },
  {
    prefix: "planningStationWeeklyUpdates",
    product: "eMariner Planning Station",
    service: "AVCS/ADP/eNP Weekly Updates",
    subtitle: "Weekly automatic updates per vessel per year",
    usesPayPerUseAmount: true,
  },
  {
    prefix: "planningStationEps",
    product: "eMariner Planning Station",
    service: "ePS – Planning Station",
    subtitle: "ENC Viewer, Digital catalogue, A to B automatic chart ordering tool",
    usesPayPerUseAmount: true,
  },
  {
    prefix: "planningStationEpsVas",
    product: "eMariner Planning Station",
    service: "ePS – Value Added Services",
    subtitle: "Passage Planning reports, Weather, Environment Manager",
    usesPayPerUseAmount: true,
  },
  {
    prefix: "planningStationEns",
    product: "eMariner Planning Station",
    service: "eNS – Emergency Navigation System",
    subtitle: "Replaces take-me-home paper charts; live vessel position via Wi-Fi dongle",
    hasWifiDongle: true,
    usesPayPerUseAmount: true,
  },
  {
    prefix: "eNtmSupport",
    product: "eMariner eNTM",
    service: "eMariner Support Services",
    subtitle: "Flag State and IMO circulars, Navigation Warnings, New Edition Information for Standard Library publications, notice to mariners, 24x7 Team viewer, phone, email and Whats App support",
    usesPayPerUseAmount: true,
  },
  {
    prefix: "avcsOptimizationEnc",
    product: "AVCS Optimization Services",
    service: "ENC Optimization",
    subtitle: "This service ensures that CNC takes the responsibility of optimizing the ENCs ordered by the vessel thus ensuring that only those ENCs required for their safe navigation are ordered.",
    usesPayPerUseAmount: true,
  },
];

export interface OtherServiceProductGroup {
  product: string;
  icon: React.ReactNode;
  services: OtherServiceDef[];
}

export const VISIBLE_OTHER_SERVICE_CATALOG: OtherServiceDef[] =
  OTHER_SERVICE_CATALOG.filter((s) => !s.hidden);

export const OTHER_SERVICES_BY_PRODUCT: OtherServiceProductGroup[] = [
  {
    product: "Standard Library Publications",
    icon: <BookOutlined />,
    services: VISIBLE_OTHER_SERVICE_CATALOG.filter(
      (s) => s.product === "Standard Library Publications",
    ),
  },
  {
    product: "eMariner Planning Station",
    icon: <CompassOutlined />,
    services: VISIBLE_OTHER_SERVICE_CATALOG.filter(
      (s) => s.product === "eMariner Planning Station",
    ),
  },
  {
    product: "eMariner eNTM",
    icon: <SafetyOutlined />,
    services: VISIBLE_OTHER_SERVICE_CATALOG.filter(
      (s) => s.product === "eMariner eNTM",
    ),
  },
  {
    product: "AVCS Optimization Services",
    icon: <ApartmentOutlined />,
    services: VISIBLE_OTHER_SERVICE_CATALOG.filter(
      (s) => s.product === "AVCS Optimization Services",
    ),
  },
];

export const OTHER_PRODUCT_COLORS: Record<string, string> = {
  "Standard Library Publications": "orange",
  "eMariner Planning Station": "magenta",
  "eMariner eNTM": "geekblue",
  "AVCS Optimization Services": "volcano",
};

export const OTHER_SUBSCRIPTION_TYPES = [
  { label: "Pay Per Use", value: "Pay Per Use" },
  { label: "Flat Fee", value: "Flat Fee" },
];

export const OTHER_STATUS_OPTIONS = [
  { label: "active", value: "active" },
  { label: "inactive", value: "inactive" },
];

export const isFlatFeeType = (t?: string): boolean =>
  t === "Flat Fee" || (!!t && t.startsWith("Flat Fee Service"));

export const isPayPerUseType = (t?: string): boolean =>
  t === "Pay Per Use" || t === "Pay Per Use - Quotation";

export const readOtherField = <T,>(
  record: unknown,
  name: string,
): T | undefined =>
  (record as Record<string, unknown> | undefined | null)?.[name] as
    | T
    | undefined;

export const fmtAnnualFee = (
  svc: OtherServiceDef,
  record: unknown,
): string => {
  const type = readOtherField<string>(record, `${svc.prefix}SubscriptionType`);
  if (!isFlatFeeType(type)) return "—";
  const fee = readOtherField<number>(
    record,
    `${svc.prefix}AnnualFlatFeePerVessel`,
  );
  return formatMoney(null, fee);
};

export const fmtCurrency = (svc: OtherServiceDef, record: unknown): string => {
  const type = readOtherField<string>(record, `${svc.prefix}SubscriptionType`);
  if (!isFlatFeeType(type)) return "—";
  return readOtherField<string>(record, `${svc.prefix}Currency`) ?? "—";
};

export const fmtDiscountOrAmount = (
  svc: OtherServiceDef,
  record: unknown,
): string => {
  const type = readOtherField<string>(record, `${svc.prefix}SubscriptionType`);
  if (!isPayPerUseType(type)) return "—";
  if (svc.usesPayPerUseAmount) {
    const amount = readOtherField<number>(
      record,
      `${svc.prefix}PayPerUseAmount`,
    );
    if (amount == null) return "—";
    const currency = readOtherField<string>(record, `${svc.prefix}Currency`);
    return formatMoney(currency, amount);
  }
  const pct = readOtherField<number>(
    record,
    `${svc.prefix}DiscountPercentage`,
  );
  return pct == null ? "—" : `${pct}%`;
};

export const fmtAvcsFlatFee = (
  amount: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (amount == null) return "AVCS Flat Fee: —";
  return `AVCS Flat Fee: ${formatMoney(currency, amount)}`;
};

export const fmtWifiDongle = (record: unknown): string | undefined => {
  if (!WIFI_DONGLE_VISIBLE) return undefined;
  const cost = readOtherField<number>(
    record,
    "planningStationEnsWifiDongleOneTimeCost",
  );
  if (!cost || cost <= 0) return undefined;
  const currency = readOtherField<string>(
    record,
    "planningStationEnsWifiDongleCurrency",
  );
  return `Wi-Fi Dongle (one-time): ${formatMoney(currency, cost)}`;
};
